import React, { createContext, useContext } from 'react';
import { useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import useReduxSelector from '@/Framework/hooks/useReduxSelector';
import { fieldChange } from '@/Framework/UI/Organisms/Filters/Checkboxer/actions';
import { useStratsSummaryFilters } from './useStratsSummaryFilters';
import { useStratsTable } from './useStratsTable';
import { usePopover } from './usePopover';
import { useStratsFilters } from './useStratsFilters';
import { useStratsMetadata } from './useStratsMetadata';
import { ICheckboxState } from '@/Framework/UI/Organisms/Filters/Checkboxer/types';
import { IGroup, IStrats, ISummaryFilters, IStratsMetadata } from '@/finsight/infrastructure/repository/StratsRepository/types';

interface IReduxProps {
  checkboxes: ICheckboxState,
}

interface IUseStratsProps {
  groups: IGroup[],
  groupedStrats: IStrats,
  summaryFilters: ISummaryFilters[],
  metadata: IStratsMetadata,
}

const stateSelector = (state): IReduxProps => ({
  checkboxes: state.checkboxes,
});

export const useStrats = (initial: IUseStratsProps) => {
  const dispatch = useDispatch();
  const { query: { fileId } } = useRouter();
  const {
    resetAllStratsFilters,
    filterStratsByRangeIdOnlyInner,
    rangeExcludedIds,
    filterStratsByRangeId,
    toggleRanges,
  } = useStratsFilters();
  const {
    summaryFiltersFetching, summaryFilters, getSummaryByFilters,
  } = useStratsSummaryFilters(initial.summaryFilters);
  const { metadata } = useStratsMetadata(initial.metadata);
  const {
    getGroups, groupedStrats, groups, isStratsFetching,
  } = useStratsTable({ rangeExcludedIds, initialGroups: initial.groups, initialGroupedStrats: initial.groupedStrats });
  const { popover, setPopoverState, resetPopoverState, updatePopoverPosition } = usePopover();
  const { checkboxes } = useReduxSelector(stateSelector);

  const applyFilter = () => {
    resetPopoverState();
    getGroups(fileId);
    getSummaryByFilters(fileId, rangeExcludedIds);
  };

  const onResetAllFilters = () => {
    resetPopoverState();
    resetAllStratsFilters();
    getSummaryByFilters(fileId);
    getGroups(fileId);

    Object.keys(checkboxes).forEach((key) => {
      if (!checkboxes[key].checked) {
        dispatch(fieldChange({
          ...checkboxes[key],
          name: key,
          checked: true,
        }));
      }
    });
  };

  const filterStratsByRangeIdOnly = (
      checkboxFieldName: string, parentCheckboxFieldName: string, siblingRanges: number[], checkboxes: ICheckboxState,
  ) => {
    filterStratsByRangeIdOnlyInner(
        checkboxFieldName,
        parentCheckboxFieldName,
        siblingRanges,
        groupedStrats,
        checkboxes,
    );
  };

  return {
    resetAllStratsFilters,
    rangeExcludedIds,
    filterStratsByRangeId,
    toggleRanges,
    filterStratsByRangeIdOnly,
    metadata,
    onResetAllFilters,
    applyFilter,
    isStratsFetching,
    groups,
    getGroups,
    groupedStrats,
    summaryFiltersFetching,
    summaryFilters,
    getSummaryByFilters,
    popover,
    setPopoverState,
    resetPopoverState,
    updatePopoverPosition,
  };
};

type StratsContextType = ReturnType<typeof useStrats>;

export const StratsContext = createContext<StratsContextType>(null);

export const useStratsContext = () => {
  const context = useContext(StratsContext);
  if (!context) {
    throw new Error('useStratsContext must be used within the DealContext');
  }
  return context;
};

interface IProps {
  initial: any,
  children: React.ReactNode,
}

const StratsContextProvider = ({ initial, children }: IProps) => (
  <StratsContext.Provider value={ useStrats(initial) }>
    { children }
  </StratsContext.Provider>
);

export default StratsContextProvider;
